/**
 * Unit conversion and formatting — the web's single choke point.
 *
 * Storage is canonically kilograms and centimetres (spec D8): `lb = kg / 0.45359237`
 * is exact, free and true forever, so a converted value is never sent to the API.
 * This module mirrors `api/platform/units.py` and `GroundworkKit/Sources/GroundworkKit/Units.swift`
 * symbol for symbol.
 *
 * This module imports NOTHING — not even from `format.ts`. The dependency runs one
 * way: `format.ts` → here. `format.ts` owns totality (no screen may ever show
 * `NaN`, `undefined` or `Infinity`) and wraps these; these just convert. Reversing
 * or crossing that arrow makes a cycle, and this pair has form: see `format.ts`'s
 * header on why `EASE_OUT` had to move out to `./motion`.
 */

/** Exact by definition. Never round these. */
export const KG_PER_LB = 0.45359237
export const CM_PER_IN = 2.54
export const LB_PER_ST = 14

export type LoadUnit = 'kg' | 'lb'
export type BodyWeightUnit = 'kg' | 'lb' | 'st'
export type HeightUnit = 'cm' | 'ftin'
export type HeightStyle = 'compact' | 'spoken'

/**
 * Nearest whole number, with an exact half going AWAY FROM ZERO (#177).
 *
 * `Math.round` is not that: it breaks ties toward +Infinity, so
 * `Math.round(-150.5)` is -150 where `api/platform/units.py` and `Units.swift` both
 * answer -151. The owner's ruling is half away from zero on EVERY path, and
 * this is the only place the three implementations could still disagree.
 *
 * No weight or height this app renders is negative, so nothing on screen moves.
 * It is here because "every path" was the ruling, and a rule that is true only
 * for the inputs someone happened to think of is the drift the golden table
 * exists to end. `contracts/formatting-golden.json` pins a negative load and a
 * negative height; `formatBodyWeight`'s stone compound is deliberately NOT
 * pinned for negatives — see its own note.
 */
function roundHalfAway(value: number): number {
  return value < 0 ? -Math.round(-value) : Math.round(value)
}

/** Kilograms to pounds. Exact; no rounding happens here. */
export function toLb(kg: number): number {
  return kg / KG_PER_LB
}

/** Pounds to kilograms. Exact; no rounding happens here. */
export function toKg(lb: number): number {
  return lb * KG_PER_LB
}

/** `80` → "80", `82.5` → "82.5". Gym numbers, matching iOS `cleanWeight`. */
function cleanKg(kg: number): string {
  return Number.isInteger(kg) ? String(kg) : kg.toFixed(1)
}

/** A lifted load in the user's unit. Pounds render whole (D4). */
export function formatLoad(kg: number, unit: LoadUnit): string {
  return unit === 'lb' ? `${roundHalfAway(toLb(kg))} lb` : `${cleanKg(kg)} kg`
}

/**
 * `formatLoad`, split into a value and a trailing unit word — same shape and
 * same reason as `formatBodyWeightParts` below: a set-grid cell or a hero
 * metric renders the number and its unit as two separately-styled JSX nodes
 * (a `tabular-nums` value, a smaller dim unit suffix), and one string can't
 * serve that without the unit inheriting the numeral's size. A load never
 * takes the stone-style compound `formatBodyWeightParts` has to special-case,
 * so this is the straight two-way split.
 */
export function formatLoadParts(kg: number, unit: LoadUnit): { value: string; unit: string } {
  return unit === 'lb'
    ? { value: String(roundHalfAway(toLb(kg))), unit: 'lb' }
    : { value: cleanKg(kg), unit: 'kg' }
}

/** Body mass. Stone is a compound (D2), and the target-weight field takes pounds (D12). */
export function formatBodyWeight(kg: number, unit: BodyWeightUnit): string {
  if (unit === 'st') {
    // Round the TOTAL pounds first, then split. Splitting first and rounding the
    // remainder is how you get "13 st 14 lb".
    //
    // DEFINED FOR NON-NEGATIVE MASS ONLY, and deliberately not pinned for
    // negatives in the golden table. The divergence there is structural rather
    // than a rounding rule: Python's `divmod(-151, 14)` gives (-11, 3) while
    // this floor-and-modulo gives -11 and -11. A body mass below zero is not a
    // value any client should render, so the two are left disagreeing about an
    // input neither should receive, rather than given a shared answer that
    // implies it is meaningful.
    const total = roundHalfAway(toLb(kg))
    const st = Math.floor(total / LB_PER_ST)
    const lb = total % LB_PER_ST
    // Ruling R12: never print a zero LEADING component -- nobody says "zero
    // stone seven pounds", the same rule that stops anyone writing "0 h
    // 5 min". This is structural, not a magnitude threshold: it's "drop a
    // zero leading component," not "small values format differently," so it
    // doesn't need retuning as the data changes. The trailing `lb` component
    // is kept even when it's zero ("1 st 0 lb") -- that's not a leading
    // component, and dropping it would silently understate the true total.
    return st === 0 ? `${lb} lb` : `${st} st ${lb} lb`
  }
  return unit === 'lb' ? `${roundHalfAway(toLb(kg))} lb` : `${cleanKg(kg)} kg`
}

/**
 * `formatBodyWeight`, split into a value and a trailing unit word, for a hero
 * figure and a small dim unit that render as two separate JSX nodes (a large
 * `clamp()`-sized numeral, a `text-base` suffix) — one string can't serve
 * that without the unit inheriting the numeral's type scale.
 *
 * The stone compound ("13 st 4 lb") is two numbers and two unit words, so
 * there is no single trailing "unit" to split off without mangling it into
 * "13 st" + "4 lb" across differently-sized text. It stays whole in `value`;
 * `unit` is `''` rather than a guess, and a call site should render it only
 * when non-empty.
 */
export function formatBodyWeightParts(
  kg: number,
  unit: BodyWeightUnit,
): { value: string; unit: string } {
  if (unit === 'st') return { value: formatBodyWeight(kg, unit), unit: '' }
  return unit === 'lb'
    ? { value: String(roundHalfAway(toLb(kg))), unit: 'lb' }
    : { value: cleanKg(kg), unit: 'kg' }
}

/**
 * Height in the user's unit.
 *
 * `style` is imperial-only and defaults to `spoken` (D11), so a call site that
 * forgets the argument gets the rendering a screen reader announces correctly.
 * `compact` has exactly ONE caller — the first-run ruler readout, where the box
 * is fixed-width — and a test names it.
 */
export function formatHeight(
  cm: number,
  unit: HeightUnit,
  style: HeightStyle = 'spoken',
): string {
  if (unit !== 'ftin') return `${roundHalfAway(cm)} cm`
  const total = roundHalfAway(cm / CM_PER_IN)
  const feet = Math.floor(total / 12)
  const inches = total % 12
  return style === 'compact' ? `${feet}'${inches}"` : `${feet} ft ${inches} in`
}

/**
 * A cm height, split into a feet/inches PAIR rather than `formatHeight`'s
 * single string -- for a caller that needs the two numbers as separate,
 * independently-typable values, not a rendered sentence.
 *
 * Originally private to `PersonalTab.tsx` (Task 7); moved here in Task 9's
 * fix round so `HeightStep.tsx`'s first-run ruler can share the exact same
 * conversion rather than a second copy of it drifting into existence. Every
 * behaviour is unchanged from the original, `height_cm` being a NOT NULL
 * `int` column is still why `Math.round` runs before the split, not after.
 */
export function cmToFeetInches(cm: number): { feet: number; inches: number } {
  const totalIn = roundHalfAway(cm / CM_PER_IN)
  return { feet: Math.floor(totalIn / 12), inches: totalIn % 12 }
}

/**
 * `cmToFeetInches`'s inverse. Deliberately a PURE, unclamped conversion --
 * see `PersonalTab.tsx`'s own "Fix round 1, I1" comment (Task 7's fix round,
 * not Task 9's -- the two are unrelated findings that happen to share a
 * label) for why a per-field min/max on feet and inches cannot express "the
 * total stays in range": the two fields' reachable SUM (36-95 in / 91-241
 * cm) is wider than `MIN_HEIGHT_CM`-`MAX_HEIGHT_CM`, so range-checking
 * belongs at the call site (`heightRejection` / `isHeightInRange`), never
 * inside this function.
 */
export function feetInchesToCm(feet: number, inches: number): number {
  return roundHalfAway((feet * 12 + inches) * CM_PER_IN)
}
